import React, { useState } from 'react';
import { Modal, Image, Tabs, Space, Button, Typography, Tag, Divider } from 'antd';
import {
  DownloadOutlined,
  ExpandOutlined,
  CompressOutlined,
  InfoCircleOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { ProcessedImage, FileInfo, ImagePreviewProps } from '../types';
import { FileUtils } from '../utils/fileUtils';
import './ImagePreview.css';

const { TabPane } = Tabs;
const { Text, Title } = Typography;

export const ImagePreview: React.FC<ImagePreviewProps> = ({
  image,
  visible,
  onClose,
  showComparison = true
}) => {
  const [activeTab, setActiveTab] = useState('original');
  const [fullscreen, setFullscreen] = useState(false);

  // 判断是否为已处理的图片
  const isProcessedImage = (img: ProcessedImage | FileInfo): img is ProcessedImage => {
    return 'processedBlob' in img;
  };

  const processedImage = isProcessedImage(image) ? image : null;
  const fileInfo = processedImage ? processedImage.originalFile : image;

  // 处理下载
  const handleDownload = (type: 'original' | 'processed') => {
    if (type === 'original' && processedImage) {
      FileUtils.downloadFile(processedImage.originalFile.file, processedImage.originalFile.name);
    } else if (type === 'original' && !processedImage) {
      const fileData = image as FileInfo;
      FileUtils.downloadFile(fileData.file, fileData.name);
    } else if (type === 'processed' && processedImage) {
      FileUtils.downloadFile(
        processedImage.processedBlob,
        processedImage.downloadName
      );
    }
  };

  // 格式化文件信息
  const formatImageInfo = (img: ProcessedImage | FileInfo) => {
    if (isProcessedImage(img)) {
      return {
        originalSize: FileUtils.formatFileSize(img.originalFile.size),
        processedSize: FileUtils.formatFileSize(img.processedBlob.size),
        originalDimensions: `${img.originalDimensions.width} × ${img.originalDimensions.height}`,
        processedDimensions: `${img.processedDimensions.width} × ${img.processedDimensions.height}`,
        processingTime: `${img.processingTime.toFixed(0)}ms`,
        compressionRatio: ((1 - img.processedBlob.size / img.originalFile.size) * 100).toFixed(1),
        scale: (img.position.scale * 100).toFixed(1)
      };
    } else {
      return {
        originalSize: FileUtils.formatFileSize(img.size),
        originalType: FileUtils.getFileTypeDescription(img.file)
      };
    }
  };

  const imageInfo = formatImageInfo(image);

  return (
    <Modal
      title={
        <div className="preview-modal-title">
          <PictureOutlined style={{ marginRight: 8 }} />
          <span>图片预览</span>
          {processedImage && (
            <Tag color="green" style={{ marginLeft: 8 }}>
              已处理
            </Tag>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={fullscreen ? '95vw' : 900}
      style={{ top: fullscreen ? 20 : undefined }}
      className={`image-preview-modal ${fullscreen ? 'fullscreen' : ''}`}
      footer={
        <div className="preview-modal-footer">
          <Space>
            <Button
              icon={fullscreen ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={() => setFullscreen(!fullscreen)}
            >
              {fullscreen ? '退出全屏' : '全屏预览'}
            </Button>

            {processedImage && (
              <>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload('original')}
                >
                  下载原图
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload('processed')}
                >
                  下载处理后图片
                </Button>
              </>
            )}

            {!processedImage && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload('original')}
              >
                下载图片
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <div className="preview-modal-content">
        {showComparison && processedImage ? (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="preview-tabs"
          >
            <TabPane tab="原图" key="original">
              <div className="image-container">
                <Image
                  src={processedImage ? processedImage.originalFile.preview : (fileInfo as FileInfo).preview}
                  alt="原图"
                  style={{ maxWidth: '100%', maxHeight: 400 }}
                  preview={false}
                />
              </div>
            </TabPane>

            <TabPane tab="处理后" key="processed">
              <div className="image-container">
                <Image
                  src={processedImage.processedUrl}
                  alt="处理后"
                  style={{ maxWidth: '100%', maxHeight: 400 }}
                  preview={false}
                />
              </div>
            </TabPane>

            <TabPane tab="对比" key="comparison">
              <div className="image-comparison">
                <div className="comparison-item">
                  <Title level={5}>原图</Title>
                  <Image
                    src={processedImage ? processedImage.originalFile.preview : (fileInfo as FileInfo).preview}
                    alt="原图"
                    style={{ maxWidth: '100%', maxHeight: 300 }}
                    preview={false}
                  />
                </div>
                <div className="comparison-item">
                  <Title level={5}>处理后</Title>
                  <Image
                    src={processedImage.processedUrl}
                    alt="处理后"
                    style={{ maxWidth: '100%', maxHeight: 300 }}
                    preview={false}
                  />
                </div>
              </div>
            </TabPane>
          </Tabs>
        ) : (
          <div className="image-container">
            <Image
              src={processedImage ? processedImage.processedUrl : (fileInfo as FileInfo).preview}
              alt={processedImage ? processedImage.originalFile.name : (fileInfo as FileInfo).name}
              style={{ maxWidth: '100%', maxHeight: 400 }}
              preview={false}
            />
          </div>
        )}

        {/* 图片信息 */}
        <Divider />
        <div className="image-info-section">
          <Title level={5}>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            图片信息
          </Title>

          <div className="info-grid">
            <div className="info-item">
              <Text type="secondary">文件名:</Text>
              <Text strong>{processedImage ? processedImage.originalFile.name : (fileInfo as FileInfo).name}</Text>
            </div>

            <div className="info-item">
              <Text type="secondary">原图大小:</Text>
              <Text>{imageInfo.originalSize}</Text>
            </div>

            {processedImage && (
              <>
                <div className="info-item">
                  <Text type="secondary">处理后大小:</Text>
                  <Text>{imageInfo.processedSize}</Text>
                </div>

                <div className="info-item">
                  <Text type="secondary">压缩率:</Text>
                  <Text>{imageInfo.compressionRatio}%</Text>
                </div>

                <div className="info-item">
                  <Text type="secondary">原图尺寸:</Text>
                  <Text>{imageInfo.originalDimensions}</Text>
                </div>

                <div className="info-item">
                  <Text type="secondary">处理后尺寸:</Text>
                  <Text>{imageInfo.processedDimensions}</Text>
                </div>

                <div className="info-item">
                  <Text type="secondary">缩放比例:</Text>
                  <Text>{imageInfo.scale}%</Text>
                </div>

                <div className="info-item">
                  <Text type="secondary">处理时间:</Text>
                  <Text>{imageInfo.processingTime}</Text>
                </div>

                <div className="info-item">
                  <Text type="secondary">位置:</Text>
                  <Text>
                    X: {Math.round(processedImage.position.x)},
                    Y: {Math.round(processedImage.position.y)}
                  </Text>
                </div>

                <div className="info-item">
                  <Text type="secondary">处理时间:</Text>
                  <Text>
                    {new Date(processedImage.processedAt).toLocaleString()}
                  </Text>
                </div>
              </>
            )}

            {!processedImage && (
              <>
                <div className="info-item">
                  <Text type="secondary">文件类型:</Text>
                  <Text>{imageInfo.originalType}</Text>
                </div>

                <div className="info-item">
                  <Text type="secondary">最后修改:</Text>
                  <Text>
                    {new Date((fileInfo as FileInfo).lastModified).toLocaleString()}
                  </Text>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};